import type { ReactNode } from 'react'
import { Loader } from 'lucide-react'
import { Navigate, Outlet, ScrollRestoration } from 'react-router-dom'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { Roles } from 'src/shared/consts'
import { AuthProvider, useAuth } from 'src/shared/hooks/authProvider'
import { TEMP_AGENT_FORCE_LOGGED_IN } from 'src/shared/hooks/tempAgentAuthBypass'
import { paths } from 'src/routes/paths'
import { NavBar } from './NavBar'

const AdminLayoutContent = () => {
  const { loading, session } = useAuth()
  const { orgUser } = useOrgUsersApi()

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    )
  }

  if (!session?.user?.id) {
    return <Navigate to={paths.loginSignUp} replace />
  }

  if (!TEMP_AGENT_FORCE_LOGGED_IN && (!orgUser || orgUser?.status !== 200)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    )
  }

  const isSuperAdmin =
    TEMP_AGENT_FORCE_LOGGED_IN ||
    orgUser?.data?.role?.id === Roles.SUPERADMIN

  if (!isSuperAdmin) {
    return <Navigate to={paths.home} replace />
  }

  return (
    <>
      <ScrollRestoration />
      <NavBar mode="admin" variant="light">
        <Outlet />
      </NavBar>
    </>
  )
}

/** Gate + shell for `/admin/*` — SUPERADMIN only. */
export const AdminLayout = () => (
  <AuthProvider>
    <AdminLayoutContent />
  </AuthProvider>
)

/** Wrap a page element for SuperAdmin-only routes outside AdminLayout. */
export const RequireSuperAdmin = ({ children }: { children: ReactNode }) => {
  const { loading, session } = useAuth()
  const { orgUser } = useOrgUsersApi()

  if (
    !TEMP_AGENT_FORCE_LOGGED_IN &&
    (loading || !orgUser || orgUser?.status !== 200)
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    )
  }

  if (!session?.user?.id) {
    return <Navigate to={paths.loginSignUp} replace />
  }

  if (
    !TEMP_AGENT_FORCE_LOGGED_IN &&
    orgUser?.data?.role?.id !== Roles.SUPERADMIN
  ) {
    return <Navigate to={paths.home} replace />
  }

  return <>{children}</>
}
